import * as React from 'react'
import * as Popover from 'primitives/popover'
import * as Dialog from 'primitives/dialog'
import { useTranslations } from 'next-intl'
import { useBreakpoint } from '@repo/web-ui/hooks/use-breakpoints'
import { IconClose, IconDelete, IconMoreHoriz } from '@repo/design-system-cf/components/icons'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import { useUserCapabilitiesQuery } from '@web/gen/user'
import { useIsLoggedInQuery } from 'auth/hooks'
import { useDeleteDialog } from '../contexts/delete-dialog-context'
import { Button } from '@repo/design-system-cf/components/button'
import type { SocialComment } from 'codegen/types'
import { DeleteDialogMobile } from './dialogs/delete-post-comment-dialog'
import { tw } from 'utils/classnames'
import { Z_INDEX_MODALS } from 'constants/modals'

export function PostCommentAction({
  comment,
  onDeleted,
}: {
  comment: SocialComment
  onDeleted?: () => void
}) {
  const t = useTranslations('pages.masterfeed')
  const user = useUser()
  const { openDialog: openDeleteDialog } = useDeleteDialog()
  const { data: isLoggedIn } = useIsLoggedInQuery()
  const { data: userCapabilities } = useUserCapabilitiesQuery(undefined, {
    select: data => data?.me?.user,
  })
  const { breakpoint } = useBreakpoint()
  const isMobile = ['xs', 'sm'].includes(breakpoint ?? '')

  const [isOpen, setIsOpen] = React.useState(false)

  const isPostOwner = user.data?.id === comment.user?.id
  const SHOW_DELETE =
    !!userCapabilities?.capabilities.includes('dashboard_masterfeed') || isPostOwner

  const handleOpenDialog = () => {
    openDeleteDialog(comment.id, 'comment', { onDeleted })
  }

  if (isMobile && isLoggedIn && SHOW_DELETE) {
    return (
      <>
        <Button
          variant={'ghost'}
          onClick={() => {
            setIsOpen(true)
          }}
          data-testid="post-comment-action-button"
          iconLeft={<IconMoreHoriz className="size-6 text-cf-purple-900" />}
        />
        <PostCommentActionMobile
          comment={comment}
          isOpen={isOpen}
          closeModal={() => setIsOpen(false)}
          onDeleted={onDeleted}
        />
      </>
    )
  }

  return isLoggedIn && SHOW_DELETE ? (
    <Popover.Root>
      <Popover.Trigger asChild>
        <Button
          variant={'ghost'}
          iconLeft={<IconMoreHoriz className="size-6 text-cf-purple-900" />}
          data-testid="post-comment-action-button"
        />
      </Popover.Trigger>
      <Popover.Content
        side="bottom"
        align="end"
        className="bg-cf-white p-1 box-shadow min-w-40 rounded-2 max-w-96 shadow-[0px_2px_6px_2px_rgba(0,0,0,0.125)]"
        sideOffset={8}
      >
        <button
          type="button"
          className="flex flex-row items-center cursor-pointer justify-start p-2 gap-2 hover:bg-cf-grey-100 rounded relative w-full"
          onClick={() => handleOpenDialog()}
          data-testid="post-comment-action-delete-button"
        >
          <IconDelete className="size-4" />
          <p className="text-14 font-medium">{t('delete_comment')}</p>
        </button>
      </Popover.Content>
    </Popover.Root>
  ) : null
}

export function PostCommentActionMobile({
  isOpen,
  closeModal,
  comment,
  onDeleted,
}: {
  isOpen: boolean
  closeModal: () => void
  comment: SocialComment
  onDeleted?: () => void
}) {
  const t = useTranslations('pages.masterfeed')
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false)

  const handleDeleteDialog = () => {
    setShowDeleteDialog(true)
  }

  const closeDeleteDialog = () => {
    setShowDeleteDialog(false)
    closeModal()
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={open => !open && closeDeleteDialog()}>
      <React.Suspense fallback={null}>
        <Dialog.Portal>
          <Dialog.Body
            placement={'bottom'}
            className={tw(
              `font-poppins xl:items-center transition-transform duration-300 ease-in-out`,
              isOpen ? 'translate-y-0' : 'translate-y-full',
            )}
            overlayZIndex={Z_INDEX_MODALS.authentication.react - 1}
            zIndex={Z_INDEX_MODALS.authentication.react}
          >
            <div className="w-full md:max-w-xl rounded-t-[20px] bg-cf-white md:rounded-[12px] py-5 px-3 md:p-8 relative shadow-md">
              {!showDeleteDialog ? (
                <>
                  <div className="flex justify-between items-center pt-1 pb-4">
                    <p className="text-16 font-semibold">{t('submenu_title')}</p>
                    <Button
                      onClick={() => closeDeleteDialog()}
                      className="pointer-events-auto"
                      variant="ghost"
                      iconLeft={<IconClose className="!size-6" />}
                    />
                  </div>
                  <div className="flex flex-col gap-1 md:gap-3">
                    <button
                      type="button"
                      onClick={() => handleDeleteDialog()}
                      className="flex flex-row items-center cursor-pointer justify-start py-2 gap-2 hover:bg-cf-grey-100 rounded relative w-full"
                    >
                      <IconDelete className={'!size-4'} />
                      <p className="text-14 font-medium">{t('delete_comment')}</p>
                    </button>
                  </div>
                </>
              ) : (
                <DeleteDialogMobile
                  targetId={comment.id}
                  closeDialog={closeDeleteDialog}
                  isComment={true}
                  onDeleted={onDeleted}
                />
              )}
            </div>
          </Dialog.Body>
        </Dialog.Portal>
      </React.Suspense>
    </Dialog.Root>
  )
}
